// Склейка Ретуши: ответ модели вклеивается обратно в оригинал по маске. Лёгкий модуль отдельно
// от Retouch.js, потому что финализатор нужен на ЛЮБОЙ странице, где стор задач может довести
// задачу до конца: опрос возобновляет AppShell, а он есть в бандле каждой страницы, Retouch —
// только на своей. Поэтому AppShell импортирует этот модуль ради регистрации, Retouch — ради
// хелперов. Входы склейки (оригинал, маска, box) лежат в IndexedDB по id задачи — переживают F5,
// переход по меню и закрытую вкладку.

import { registerFinalizer } from '../../utils/jobs';
import { openDb } from '../../utils/history';
import { getProxiedUrl } from '../../utils/proxyUrl';

export const makeCanvas = (w, h) => {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
};
// src — canvas или ImageBitmap (входы, поднятые из IndexedDB)
export const cloneCanvas = (src) => {
  const c = makeCanvas(src.width, src.height);
  c.getContext('2d').drawImage(src, 0, 0);
  return c;
};
export const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous'; // иначе canvas «испачкается» и toDataURL упадёт
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Не удалось загрузить изображение'));
  img.src = src;
});

// Расширение маски на r px: сдвинутые копии по окружностям через 2 px, чтобы не было щелей
export const dilate = (mask, r) => {
  const c = cloneCanvas(mask);
  const ctx = c.getContext('2d');
  for (let rr = r; rr > 0; rr -= 2) {
    for (let a = 0; a < 16; a++) {
      ctx.drawImage(mask, Math.round(rr * Math.cos((a * Math.PI) / 8)), Math.round(rr * Math.sin((a * Math.PI) / 8)));
    }
  }
  return c;
};

// Склейка: оригинал + результат только внутри маски (расширена на 6 px и размыта на 8 px)
// box — куда в кадре лёг ответ модели (фрагмент при crop & stitch, иначе весь кадр)
// ponytail: результат растягивается в box, даже если модель чуть поменяла пропорции.
const buildComposite = (orig, mask, result, box) => {
  const { width: w, height: h } = orig;
  const b = box || { x: 0, y: 0, w, h };
  const soft = makeCanvas(w, h);
  const sctx = soft.getContext('2d');
  sctx.filter = 'blur(8px)'; // где ctx.filter нет (старый Safari) — будет жёсткий край
  sctx.drawImage(dilate(mask, 6), 0, 0);
  // блюр у края холста гасит альфу вдвое — само выделение дорисовываем непрозрачным
  sctx.filter = 'none';
  sctx.drawImage(mask, 0, 0);
  const top = makeCanvas(w, h);
  const tctx = top.getContext('2d');
  tctx.drawImage(result, b.x, b.y, b.w, b.h);
  tctx.globalCompositeOperation = 'destination-in';
  tctx.drawImage(soft, 0, 0);
  const out = cloneCanvas(orig);
  out.getContext('2d').drawImage(top, 0, 0);
  return out;
};

// --- входы склейки в IndexedDB -------------------------------------------------------------------

// Своя база, не история: у записи другая форма (два блоба + box), и чистится она по-своему.
const DB = { dbName: 'airun-retouch-inputs', store: 'inputs', version: 1 };
// Задача живёт максимум сутки (MAX_RESTORE_AGE_MS в jobs.js); всё старше трёх — мусор от
// упавших/отменённых из других мест задач.
const INPUTS_TTL_MS = 3 * 24 * 60 * 60 * 1000;

const tx = async (mode, fn) => {
  const db = await openDb(DB);
  try {
    return await new Promise((resolve, reject) => {
      const t = db.transaction(DB.store, mode);
      const req = fn(t.objectStore(DB.store));
      t.oncomplete = () => resolve(req ? req.result : undefined);
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  } finally {
    db.close();
  }
};

const toBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob вернул null'))), 'image/png');
});

// PNG, а не JPEG: вне маски склейка — это пиксели оригинала, второе сжатие их бы портило.
// Не бросает: без входов финализатор вернёт null, и в историю ляжет сырой ответ — как раньше.
export async function saveInputs(id, orig, mask, box) {
  try {
    const [o, m] = await Promise.all([toBlob(orig), toBlob(mask)]);
    await tx('readwrite', (s) => s.put({ id, orig: o, mask: m, box: box || null, timestamp: Date.now() }));
  } catch (e) {
    console.warn('retouch: входы склейки не сохранены, результат будет без склейки', e);
  }
}

export const dropInputs = (id) => tx('readwrite', (s) => s.delete(id)).catch(() => {});

// Картинка, переданная кнопкой «Ретушь» из другого инструмента. Лежит в той же базе под
// служебным ключом, а не только в памяти: после деплоя устаревшая вкладка на переходе делает
// полную перезагрузку (Gatsby сверяет хеш сборки), и переменная модуля теряется.
const PENDING_ID = '__pending';
const PENDING_TTL_MS = 2 * 60 * 1000;

export const savePending = (blob) =>
  tx('readwrite', (s) => s.put({ id: PENDING_ID, orig: blob, timestamp: Date.now() })).catch(() => {});

// Только читает: удаляет её тот экземпляр Ретуши, который реально загрузил фото (см. Retouch.js).
// Старше двух минут — не наша.
export async function peekPending() {
  try {
    const rec = await tx('readonly', (s) => s.get(PENDING_ID));
    return rec && Date.now() - rec.timestamp < PENDING_TTL_MS ? rec.orig : null;
  } catch (e) {
    return null;
  }
}

export const dropPending = () => dropInputs(PENDING_ID);

const pruneInputs = () => tx('readwrite', (s) => {
  const req = s.index('timestamp').openCursor(IDBKeyRange.upperBound(Date.now() - INPUTS_TTL_MS));
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor) return;
    cursor.delete();
    cursor.continue();
  };
  return null;
}).catch(() => {});

if (typeof window !== 'undefined') {
  registerFinalizer('retouch', async (job, rawUrl) => {
    const inputs = await tx('readonly', (s) => s.get(job.id)).catch(() => null);
    if (!inputs) return null;
    try {
      const [orig, mask, res] = await Promise.all([
        createImageBitmap(inputs.orig), createImageBitmap(inputs.mask), loadImage(getProxiedUrl(rawUrl))
      ]);
      return buildComposite(orig, mask, res, inputs.box).toDataURL('image/jpeg', 0.95);
    } finally {
      dropInputs(job.id);
    }
  });
  setTimeout(pruneInputs, 0);
}
